import { ColumnDef, createColumnHelper, Row } from '@tanstack/react-table';
import { useModelService, Measure } from 'services/model/model.service';
import { IntlShape, useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import { buildDrillDownTableCoreColumns } from 'pages/trading/outlier/DrilldownCoreColumns';
import { DrillDownTableDateGmvValues } from 'pages/trading/outlier/DrillDownTableDateRangeRow';
import { MessageSentiment } from 'pages/trading/model';
import { ShareBar } from 'pages/insights-pro/common/reports/ShareBar';
import { TRADING_ROOT } from 'pages/trading/Trading';
import { DataTable } from 'components/table/DataTable';
import { DataTableContextProvider } from 'components/table/context/DataTableContextProvider';
import { useCoreDrilldownExportConfigOptions } from 'pages/trading/outlier/DrilldownExportUtil';

export interface BrandRow extends DrillDownTableDateGmvValues {
  prdBrand: string;
}

const columnHelper = createColumnHelper<BrandRow>();
const buildColumns = (headers: string[], intl: IntlShape, measure: Measure) => [
  columnHelper.accessor('prdBrand', {
    header: () => headers[0],
    cell: info => (
      <div className="flex gap-2 items-center">
        <div className="flex gap-2 items-center">{info.getValue()}</div>
      </div>
    ),
  }),
  columnHelper.accessor(row => row.share, {
    id: 'share',
    header: () => intl.formatMessage({ id: 'trading.drilldown.column.share' }),
    cell: info => <ShareBar width={info.getValue() * 100} color="#314968" />,
  }),
  ...buildDrillDownTableCoreColumns(intl, measure),
];

export interface CategoriesTableProps {
  rows: BrandRow[];
  measure: Measure;
  sentiment?: MessageSentiment;
  detectionDate?: string;
}

export function BrandsTable({ rows, measure, sentiment = 'NEUTRAL', detectionDate = undefined }: CategoriesTableProps) {
  const { getAttributes } = useModelService();
  const [prdBrand] = getAttributes(['prdBrand']);
  const navigate = useNavigate();
  const location = useLocation();
  const intl = useIntl();

  const onRowClick = (row: Row<unknown>) => {
    const castedRow = row as Row<BrandRow>;
    navigate(`${TRADING_ROOT}/drilldown/prdBrand/${castedRow.original.prdBrand}${location.search}`);
  };
  const columns = buildColumns([prdBrand.name], intl, measure) as ColumnDef<unknown>[];

  const exportConfigOptions = useCoreDrilldownExportConfigOptions('brands');

  return (
    <DataTableContextProvider>
      <DataTable
        testId="trading-drilldown-brands-table"
        titleId="trading.analysis.level.header.BRAND"
        subTitleDecorator={header =>
          `${header}${detectionDate ? ` ${intl.formatMessage({ id: 'trading.drilldown.detection.date' })} ${detectionDate}` : ''}`
        }
        rows={rows}
        columns={columns}
        onRowClick={onRowClick}
        exportConfigOptions={exportConfigOptions}
        defaultSortingState={[{ id: 'onDayComparisonGmv', desc: sentiment !== 'NEGATIVE' }]}
      />
    </DataTableContextProvider>
  );
}
